
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Eye, Edit2, Trash2, AlertCircle } from 'lucide-react';
import { suppliersAPI } from '../../services/garageApi';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Debounced search to avoid excessive API calls
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        search: debouncedSearch,
        page: currentPage,
        per_page: itemsPerPage
      };

      const response = await suppliersAPI.getAll(params);

      const suppliersData = response?.data || [];

      if (!Array.isArray(suppliersData)) {
        throw new Error('Invalid response format');
      }

      setSuppliers(suppliersData);

      // Handle pagination metadata
      if (response.pagination) {
        setTotalPages(response.pagination.last_page || 1);
        setTotalItems(response.pagination.total || suppliersData.length);
      } else {
        // Fallback if no pagination info
        setTotalPages(1);
        setTotalItems(suppliersData.length);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setError('Failed to load suppliers. Please try again.');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleDelete = useCallback(async (id) => {
    const supplier = suppliers.find(s => s.id === id);
    const supplierName = supplier?.name || `Supplier #${id}`;

    console.log('🔍 Suppliers: Attempting to delete supplier:', { id, supplierName, supplier });

    if (!window.confirm(`Are you sure you want to delete ${supplierName}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(id);
      console.log('🔍 Suppliers: Calling delete API for supplier ID:', id);
      const response = await suppliersAPI.delete(id);
      console.log('🔍 Suppliers: Delete API response:', response);

      // Optimistic update
      setSuppliers(prev => prev.filter(supplier => supplier.id !== id));
      console.log('✅ Suppliers: Successfully deleted supplier from list');
    } catch (error) {
      console.error('❌ Suppliers: Error deleting supplier:', error);
      console.error('❌ Suppliers: Delete error details:', {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      setError('Failed to delete supplier. Please try again.');
      fetchSuppliers();
    } finally {
      setDeleting(null);
    }
  }, [suppliers, fetchSuppliers]);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleRetry = useCallback(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  // Memoized empty state
  const emptyState = useMemo(() => (
      <tr>
        <td colSpan="5" className="px-6 py-12 text-center">
          <div className="text-gray-500">
            <p className="text-lg font-medium">No suppliers found</p>
            {search && (
                <p className="text-sm mt-1">
                  Try adjusting your search terms or{' '}
                  <button
                      onClick={() => setSearch('')}
                      className="text-blue-600 hover:text-blue-800 underline"
                  >
                    clear search
                  </button>
                </p>
            )}
          </div>
        </td>
      </tr>
  ), [search]);

  // Loading state
  if (loading && suppliers.length === 0) {
    return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading suppliers...</span>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
            <p className="text-gray-600">Manage your supplier database</p>
          </div>
          <Link
              to="/suppliers/create"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Supplier
          </Link>
        </div>

        {/* Error Message */}
        {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                  <button
                      onClick={handleRetry}
                      className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* Suppliers Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {/* Search Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Suppliers
              </h3>
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="Search suppliers..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
              {suppliers.length > 0 ? (
                  suppliers.map((supplier) => (
                      <tr key={supplier.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <span className="text-green-600 font-medium text-sm">
                                    {supplier.name ? supplier.name.charAt(0).toUpperCase() : 'S'}
                                  </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {supplier.name || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {supplier.contact_person || 'No contact'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {supplier.email || 'No email'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {supplier.phone || 'No phone'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {supplier.address || 'No address'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link
                                to={`/suppliers/${supplier.id}`}
                                className="group inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 transform hover:scale-110"
                                title="View Supplier"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>

                            <Link
                                to={`/suppliers/${supplier.id}/edit`}
                                className="group inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-green-600 hover:bg-green-50 transition-all duration-200 transform hover:scale-110"
                                title="Edit Supplier"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Link>

                            <button
                                onClick={() => handleDelete(supplier.id)}
                                disabled={deleting === supplier.id}
                                className="group inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                title="Delete Supplier"
                            >
                              {deleting === supplier.id ? (
                                  <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full" />
                              ) : (
                                  <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                  ))
              ) : (
                  emptyState
              )}
              </tbody>
            </table>
          </div>

          {/* Loading overlay for subsequent requests */}
          {loading && suppliers.length > 0 && (
              <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={goToPrevious}
                  disabled={!hasPrevious}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={goToNext}
                  disabled={!hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                    <span className="font-medium">{totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={goToPrevious}
                      disabled={!hasPrevious}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={goToNext}
                      disabled={!hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default Suppliers;